/**
 * Custom element that handles displaying notification messages
 * @extends HTMLElement
 */
class NotificationsWrapper extends HTMLElement {
  /**
   * Called when element is added to DOM. Initializes variables and event listeners.
   */
  connectedCallback() {
    this.initVariables();
    this.addEventListeners();
  }

  /**
   * Initializes template and notifications wrapper elements
   * @private
   */
  initVariables() {
    this.template = this.querySelector('#notification-template').cloneNode(
      true
    );

    this.template.removeAttribute('id');
    this.template.classList.add('notification-wrapper');
    this.notificationsWrapper = this.querySelector(
      '[data-notifications-wrapper]'
    );
  }

  /**
   * Adds event listeners for notification events
   * @private
   */
  addEventListeners() {
    document.addEventListener(
      'pxl:notification',
      this.addNotification.bind(this)
    );
  }

  /**
   * Adds a new notification element
   * @param {CustomEvent} event - Event containing notification details
   * @private
   */
  addNotification(event) {
    const notification = event.detail.notification;
    const notificationWrapper = this.template.cloneNode(true);
    notificationWrapper.querySelector('[data-notification-text]').textContent =
      notification;
    this.notificationsWrapper.append(notificationWrapper);

    // Setup observer to trigger fade-in animation when notification is visible
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            notificationWrapper.classList.add('fade-in');
          }
        }
      },
      { threshold: 0 }
    );

    observer.observe(notificationWrapper);

    const closeButton = notificationWrapper.querySelector('[data-close]');
    closeButton.addEventListener('click', () => {
      closeButton.removeEventListener('click', this.removeNotification);
      this.removeNotification(notificationWrapper, observer, 0);
    });

    this.removeNotification(notificationWrapper, observer);
  }

  /**
   * Removes a notification element with animation
   * @param {HTMLElement} notificationWrapper - The notification element to remove
   * @param {IntersectionObserver} observer - The observer watching the notification
   * @param {number} [timeout=3000] - Time in ms before notification is removed
   * @private
   */
  removeNotification(notificationWrapper, observer, timeout = 3000) {
    if (!notificationWrapper) return;

    setTimeout(() => {
      notificationWrapper.classList.add('removing');
      observer.unobserve(notificationWrapper);
      setTimeout(() => {
        notificationWrapper.remove();
      }, 300);
    }, timeout);
  }
}

customElements.get('notifications-wrapper') ||
  customElements.define('notifications-wrapper', NotificationsWrapper);
